// ApiKeysModal — opened from the attendance Settings drawer's "Manage API
// keys" button, modelled on the Forms page's Responses modal: a DataTable of
// the company's keys (POST api-keys/list, server-paged, newest first) with
// "+ Create API key" opening CreateApiKeyModal.
//
// Revoke (active keys) and Delete (revoked keys) run from each row's kebab
// through a confirm; both go to the bulk endpoints with a one-element array.
// There is no multi-select — the design draws none.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, ConfirmModal, DataTable, Icon, Modal, TableFooter } from '@/components/common';
import {
  useBulkDeleteApiKeysMutation,
  useBulkRevokeApiKeysMutation,
  useGetApiKeysQuery,
  type ApiKeyItem,
} from '@/api/endpoints/apiKeysApi';
import type { ApiErrorShape } from '@/api/types';
import useStickyTotal from '@/hooks/useStickyTotal';
import { useApiKeyColumns } from './apiKeyColumns';
import CreateApiKeyModal from './CreateApiKeyModal';

interface ApiKeysModalProps {
  open: boolean;
  onClose: () => void;
}

/** A confirm awaiting the user: which action, on which key. */
interface PendingAction {
  kind: 'revoke' | 'delete';
  ids: string[];
}

const ApiKeysModal = ({ open, onClose }: ApiKeysModalProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const [bulkRevoke, { isLoading: revoking }] = useBulkRevokeApiKeysMutation();
  const [bulkDelete, { isLoading: deleting }] = useBulkDeleteApiKeysMutation();

  const { data, currentData, isLoading, isFetching, isError } = useGetApiKeysQuery(
    { page, limit },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const rows = data?.items ?? [];
  const total = useStickyTotal(currentData?.total, page);

  // Reset to a clean state each time the modal (re)opens.
  useEffect(() => {
    if (open) {
      setPage(1);
      setCreateOpen(false);
      setPending(null);
    }
  }, [open]);

  // An action can empty the page we're standing on — fall back to the last one
  // that still has rows rather than showing an empty table with a live pager.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / limit));
    if (page > last) setPage(last);
  }, [total, limit, page]);

  const runPending = async () => {
    if (!pending) return;
    const { kind, ids } = pending;
    try {
      if (kind === 'revoke') await bulkRevoke(ids).unwrap();
      else await bulkDelete(ids).unwrap();
      toast.success(
        t(kind === 'revoke' ? 'api_keys_revoked' : 'api_keys_deleted', { count: ids.length }),
      );
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
    setPending(null);
  };

  const columns = useApiKeyColumns({
    onRevoke: (row) => setPending({ kind: 'revoke', ids: [row.id] }),
    onDelete: (row) => setPending({ kind: 'delete', ids: [row.id] }),
  });

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const busy = revoking || deleting;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        className='form-responses api-keys'
        icon='key-round'
        tone='success'
        title={t('api_keys_title')}
        subtitle={t('api_keys_subtitle')}
        size='lg'
      >
        <div className='form-responses__toolbar'>
          <Button iconStart='plus' onClick={() => setCreateOpen(true)}>
            {t('api_keys_add')}
          </Button>
        </div>

        <DataTable<ApiKeyItem>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1100}
          empty={
            <>
              <Icon name='key-round' size={26} />
              {t('api_keys_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('forms_items_per_page')}
          rangeText={t('api_keys_range', { from, to, total })}
        />
      </Modal>

      <CreateApiKeyModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {pending && (
        <ConfirmModal
          open
          onClose={() => setPending(null)}
          onConfirm={runPending}
          tone='danger'
          icon={pending.kind === 'revoke' ? 'ban' : 'trash-2'}
          title={t(pending.kind === 'revoke' ? 'api_keys_revoke_title' : 'api_keys_delete_title', {
            count: pending.ids.length,
          })}
          description={t(
            pending.kind === 'revoke' ? 'api_keys_revoke_body' : 'api_keys_delete_body',
            { count: pending.ids.length },
          )}
          confirmLabel={t(
            pending.kind === 'revoke' ? 'api_keys_action_revoke' : 'api_keys_action_delete',
          )}
          confirmVariant='danger'
          loading={busy}
        />
      )}
    </>
  );
};

export default ApiKeysModal;
